/**
 * Initialize parallax scrolling effect for elements with data-parallax="true".
 *
 * Uses CSS custom property --scroll-offset that elements can use in their styles.
 * Speed can be customized per-element with data-parallax-speed attribute.
 *
 * @example
 * ```html
 * <div data-parallax="true" data-parallax-speed="0.2" style="transform: translateY(var(--scroll-offset))">
 *   Parallax content
 * </div>
 * ```
 *
 * @returns A cleanup function that removes the event listeners
 */
export function initParallax(): () => void {
  const parallaxElements = document.querySelectorAll<HTMLElement>('[data-parallax="true"]');

  let ticking = false;

  function applyParallax(scrolled: number): void {
    parallaxElements.forEach((element) => {
      const speedAttr = element.getAttribute('data-parallax-speed');
      const speed = parseFloat(speedAttr ?? '0.15');
      const rate = scrolled * speed;
      element.style.setProperty('--scroll-offset', `${String(rate)}px`);
    });
  }

  // Coalesce scroll/resize events to one write per animation frame. The scroll
  // position is read once up front so layout reads never interleave with the
  // style writes, which is what triggers forced synchronous reflow.
  function onScroll(): void {
    if (ticking) return;
    ticking = true;
    const scrolled = window.scrollY;
    window.requestAnimationFrame(() => {
      applyParallax(scrolled);
      ticking = false;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  // Initial paint
  applyParallax(window.scrollY);

  return (): void => {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
  };
}
